"use client";

import { NumberField as BaseNumberField } from "@base-ui/react/number-field";
import {
  KeyboardArrowDownRounded as DecrementIcon,
  KeyboardArrowUpRounded as IncrementIcon,
} from "@mui/icons-material";
import {
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  type SxProps,
  type Theme,
} from "@mui/material";
import * as React from "react";

import { radius } from "../tokens/radius";

function SSRInitialFilled(_: BaseNumberField.Root.Props) {
  return null;
}
SSRInitialFilled.muiName = "Input";

export function NumberField({
  id: idProp,
  label,
  helperText,
  placeholder,
  startAdornment,
  endAdornment: endAdornmentProp,
  error,
  size = "medium",
  sx,
  ...other
}: BaseNumberField.Root.Props & {
  label?: React.ReactNode;
  helperText?: React.ReactNode;
  placeholder?: string;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  size?: "small" | "medium";
  error?: boolean;
  sx?: SxProps<Theme>;
}) {
  const generatedId = React.useId();
  const id = idProp ?? generatedId;

  return (
    <BaseNumberField.Root
      {...other}
      render={(props, state) => (
        <FormControl
          size={size}
          ref={props.ref}
          disabled={state.disabled}
          required={state.required}
          error={error}
          variant="outlined"
          sx={sx}
          fullWidth
        >
          {props.children}
        </FormControl>
      )}
    >
      <SSRInitialFilled {...other} />
      {label && <InputLabel htmlFor={id}>{label}</InputLabel>}
      <BaseNumberField.Input
        id={id}
        render={(props, state) => (
          <OutlinedInput
            aria-describedby={helperText ? `${id}-helper-text` : undefined}
            label={label}
            placeholder={placeholder}
            inputRef={props.ref}
            value={state.inputValue}
            onBlur={props.onBlur}
            onChange={props.onChange}
            onKeyUp={props.onKeyUp}
            onKeyDown={props.onKeyDown}
            onFocus={props.onFocus}
            slotProps={{ input: props }}
            startAdornment={
              startAdornment ? (
                <InputAdornment position="start">
                  {startAdornment}
                </InputAdornment>
              ) : undefined
            }
            endAdornment={
              <InputAdornment
                position="end"
                sx={{
                  flexDirection: "row",
                  maxHeight: "unset",
                  alignSelf: "stretch",
                  ml: 0,
                  gap: 0.5,
                }}
              >
                {endAdornmentProp}
                <InputAdornment
                  position="end"
                  sx={{
                    flexDirection: "column",
                    maxHeight: "unset",
                    alignSelf: "stretch",
                    borderLeft: "1px solid",
                    borderColor: "divider",
                    ml: 0,
                    "& button": { py: 0, flex: 1, borderRadius: radius.sm },
                  }}
                >
                  <BaseNumberField.Increment
                    render={<IconButton size={size} aria-label="Increase" />}
                  >
                    <IncrementIcon
                      fontSize={size}
                      sx={{ transform: "translateY(2px)" }}
                    />
                  </BaseNumberField.Increment>
                  <BaseNumberField.Decrement
                    render={<IconButton size={size} aria-label="Decrease" />}
                  >
                    <DecrementIcon
                      fontSize={size}
                      sx={{ transform: "translateY(-2px)" }}
                    />
                  </BaseNumberField.Decrement>
                </InputAdornment>
              </InputAdornment>
            }
            sx={{ pr: 0 }}
          />
        )}
      />
      {helperText && (
        <FormHelperText id={`${id}-helper-text`} sx={{ ml: 0 }}>
          {helperText}
        </FormHelperText>
      )}
    </BaseNumberField.Root>
  );
}
